import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Switch,
  FormControlLabel,
  TextField,
  Button,
  Grid,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  CircularProgress,
  Chip,
} from '@mui/material';
import {
  Save as SaveIcon,
  Restore as RestoreIcon,
  Flag as FlagIcon,
  Settings as SettingsIcon,
  History as HistoryIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';

interface FeatureFlag {
  id: string;
  name: string;
  description: string;
  enabled: boolean;
  lastModified: string;
  modifiedBy: string;
}

interface ConfigItem {
  key: string;
  label: string;
  value: string;
  type: 'number' | 'text';
  unit?: string;
  description: string;
}

interface ConfigHistoryEntry {
  id: string;
  timestamp: string;
  user: string;
  action: string;
  details: string;
}

const FeatureFlagsPage: React.FC = () => {
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isOffline, setIsOffline] = useState<boolean>(!navigator.onLine);

  const [featureFlags, setFeatureFlags] = useState<FeatureFlag[]>([
    {
      id: 'ff_1',
      name: 'Advanced Analytics',
      description: 'Enable advanced analytics features including power curve analysis',
      enabled: true,
      lastModified: new Date().toISOString(),
      modifiedBy: 'admin@example.com',
    },
    {
      id: 'ff_2',
      name: 'Workout Templates',
      description: 'Enable workout template creation and management for trainers',
      enabled: true,
      lastModified: new Date().toISOString(),
      modifiedBy: 'admin@example.com',
    },
    {
      id: 'ff_3',
      name: 'Real-time Sync',
      description: 'Enable real-time WebSocket synchronization for activities',
      enabled: false,
      lastModified: new Date().toISOString(),
      modifiedBy: 'admin@example.com',
    },
    {
      id: 'ff_4',
      name: 'Push Notifications',
      description: 'Enable push notifications for workout reminders and updates',
      enabled: true,
      lastModified: new Date().toISOString(),
      modifiedBy: 'admin@example.com',
    },
    {
      id: 'ff_5',
      name: 'Beta Features',
      description: 'Enable experimental beta features for testing',
      enabled: false,
      lastModified: new Date().toISOString(),
      modifiedBy: 'admin@example.com',
    },
  ]);

  const [configItems, setConfigItems] = useState<ConfigItem[]>([
    {
      key: 'cache_ttl_activities',
      label: 'Activities Cache TTL',
      value: '3600',
      type: 'number',
      unit: 'seconds',
      description: 'Time-to-live for activities cache',
    },
    {
      key: 'cache_ttl_analytics',
      label: 'Analytics Cache TTL',
      value: '1800',
      type: 'number',
      unit: 'seconds',
      description: 'Time-to-live for analytics data cache',
    },
    {
      key: 'rate_limit_api',
      label: 'API Rate Limit',
      value: '100',
      type: 'number',
      unit: 'requests/minute',
      description: 'Maximum API requests per minute per user',
    },
    {
      key: 'rate_limit_sync',
      label: 'Sync Rate Limit',
      value: '10',
      type: 'number',
      unit: 'requests/hour',
      description: 'Maximum sync requests per hour per user',
    },
    {
      key: 'max_activities_per_sync',
      label: 'Max Activities Per Sync',
      value: '200',
      type: 'number',
      unit: 'activities',
      description: 'Maximum number of activities to sync in one request',
    },
    {
      key: 'session_timeout',
      label: 'Session Timeout',
      value: '86400',
      type: 'number',
      unit: 'seconds',
      description: 'User session timeout duration',
    },
  ]);

  const [configHistory, setConfigHistory] = useState<ConfigHistoryEntry[]>([
    {
      id: 'ch_1',
      timestamp: new Date(Date.now() - 3600000).toISOString(),
      user: 'admin@example.com',
      action: 'Updated Feature Flag',
      details: 'Enabled "Real-time Sync"',
    },
    {
      id: 'ch_2',
      timestamp: new Date(Date.now() - 7200000).toISOString(),
      user: 'admin@example.com',
      action: 'Updated Configuration',
      details: 'Changed API Rate Limit from 50 to 100',
    },
    {
      id: 'ch_3',
      timestamp: new Date(Date.now() - 86400000).toISOString(),
      user: 'superuser@example.com',
      action: 'Updated Feature Flag',
      details: 'Disabled "Beta Features"',
    },
  ]);

  const [originalFeatureFlags, setOriginalFeatureFlags] = useState<FeatureFlag[]>([]);
  const [originalConfigItems, setOriginalConfigItems] = useState<ConfigItem[]>([]);

  useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    fetchData();

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const fetchData = async () => {
    setLoading(true);
    setError(null);

    try {
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 1000));

      setOriginalFeatureFlags(JSON.parse(JSON.stringify(featureFlags)));
      setOriginalConfigItems(JSON.parse(JSON.stringify(configItems)));
    } catch (err) {
      setError('Failed to load feature flags and configuration');
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFeatureFlagToggle = (flagId: string) => {
    setFeatureFlags((prev) =>
      prev.map((flag) =>
        flag.id === flagId
          ? {
              ...flag,
              enabled: !flag.enabled,
              lastModified: new Date().toISOString(),
              modifiedBy: 'current-user@example.com',
            }
          : flag
      )
    );
  };

  const handleConfigChange = (key: string, value: string) => {
    setConfigItems((prev) =>
      prev.map((item) => (item.key === key ? { ...item, value } : item))
    );
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccessMessage(null);

    try {
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 1500));

      if (isOffline) {
        throw new Error('Cannot save changes while offline');
      }

      // Add to history
      const newHistoryEntry: ConfigHistoryEntry = {
        id: `ch_${Date.now()}`,
        timestamp: new Date().toISOString(),
        user: 'current-user@example.com',
        action: 'Updated Configuration',
        details: 'Saved feature flags and configuration changes',
      };

      setConfigHistory((prev) => [newHistoryEntry, ...prev]);
      setOriginalFeatureFlags(JSON.parse(JSON.stringify(featureFlags)));
      setOriginalConfigItems(JSON.parse(JSON.stringify(configItems)));

      setSuccessMessage('Changes saved successfully');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save changes');
      console.error('Error saving changes:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleRevert = () => {
    setFeatureFlags(JSON.parse(JSON.stringify(originalFeatureFlags)));
    setConfigItems(JSON.parse(JSON.stringify(originalConfigItems)));
    setSuccessMessage('Changes reverted');
  };

  const hasChanges = () => {
    return (
      JSON.stringify(featureFlags) !== JSON.stringify(originalFeatureFlags) ||
      JSON.stringify(configItems) !== JSON.stringify(originalConfigItems)
    );
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Feature Flags & Configuration
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage system-wide feature flags and configuration settings
        </Typography>
      </Box>

      {isOffline && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          You are currently offline. Changes cannot be saved until connection is restored.
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      {hasChanges() && (
        <Alert severity="info" sx={{ mb: 3 }}>
          You have unsaved changes. Click "Save Changes" to apply them.
        </Alert>
      )}

      <Paper sx={{ p: 3, mb: 3 }}>
        <Box display="flex" alignItems="center" mb={2}>
          <FlagIcon sx={{ mr: 1 }} />
          <Typography variant="h6">Feature Flags</Typography>
        </Box>
        <Divider sx={{ mb: 2 }} />

        <Grid container spacing={2}>
          {featureFlags.map((flag) => (
            <Grid item xs={12} key={flag.id}>
              <Box
                sx={{
                  p: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Box sx={{ flex: 1 }}>
                  <Box display="flex" alignItems="center" mb={0.5}>
                    <Typography variant="subtitle1" fontWeight="medium">
                      {flag.name}
                    </Typography>
                    <Chip
                      label={flag.enabled ? 'Enabled' : 'Disabled'}
                      color={flag.enabled ? 'success' : 'default'}
                      size="small"
                      sx={{ ml: 1 }}
                    />
                  </Box>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    {flag.description}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Last modified: {format(new Date(flag.lastModified), 'PPpp')} by{' '}
                    {flag.modifiedBy}
                  </Typography>
                </Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={flag.enabled}
                      onChange={() => handleFeatureFlagToggle(flag.id)}
                      disabled={isOffline}
                    />
                  }
                  label=""
                />
              </Box>
            </Grid>
          ))}
        </Grid>
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Box display="flex" alignItems="center" mb={2}>
          <SettingsIcon sx={{ mr: 1 }} />
          <Typography variant="h6">System Configuration</Typography>
        </Box>
        <Divider sx={{ mb: 2 }} />

        <Grid container spacing={3}>
          {configItems.map((item) => (
            <Grid item xs={12} sm={6} key={item.key}>
              <TextField
                fullWidth
                label={item.label}
                type={item.type}
                value={item.value}
                onChange={(e) => handleConfigChange(item.key, e.target.value)}
                helperText={`${item.description}${item.unit ? ` (${item.unit})` : ''}`}
                disabled={isOffline}
                InputProps={{
                  endAdornment: item.unit && (
                    <Typography variant="body2" color="text.secondary">
                      {item.unit}
                    </Typography>
                  ),
                }}
              />
            </Grid>
          ))}
        </Grid>
      </Paper>

      <Box display="flex" gap={2} mb={3}>
        <Button
          variant="contained"
          color="primary"
          startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
          onClick={handleSave}
          disabled={!hasChanges() || saving || isOffline}
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
        <Button
          variant="outlined"
          startIcon={<RestoreIcon />}
          onClick={handleRevert}
          disabled={!hasChanges() || saving}
        >
          Revert Changes
        </Button>
      </Box>

      <Paper sx={{ p: 3 }}>
        <Box display="flex" alignItems="center" mb={2}>
          <HistoryIcon sx={{ mr: 1 }} />
          <Typography variant="h6">Configuration History</Typography>
        </Box>
        <Divider sx={{ mb: 2 }} />

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Timestamp</TableCell>
                <TableCell>User</TableCell>
                <TableCell>Action</TableCell>
                <TableCell>Details</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {configHistory.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    <Typography variant="body2" color="text.secondary">
                      No configuration history available
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                configHistory.map((entry) => (
                  <TableRow key={entry.id}>
                    <TableCell>
                      {format(new Date(entry.timestamp), 'PPpp')}
                    </TableCell>
                    <TableCell>{entry.user}</TableCell>
                    <TableCell>{entry.action}</TableCell>
                    <TableCell>{entry.details}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Container>
  );
};

export default FeatureFlagsPage;